import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import LoginTest from "./LoginTest";
import { confirmSignUp, forgotPassword, resendConfirmationCode, signIn, signUp,
  forgotPasswordSubmit, confirmSignInWithTOTP, enableTOTP, setupTOTP, verifyTOTP, checkIsAdmin } from "../auth/cognito";
import { mapCognitoError } from "../auth/cognitoErrorHandler";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../contexts/AuthContext";
import { Mail, Lock, Eye, EyeOff, User, Building2, Hash, ArrowRight, LoaderCircle } from "lucide-react";
import VaultLoginShell from "../components/vaultLogin/VaultLoginShell";
import "../components/loginAuth.css";

function MgPasswordField({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
  ariaLabel,
  disabled,
  showForgot = false,
  onForgot,
  onFocus,
  onBlur,
  noBottomMargin = false,
  showSubmit = false,
  reserveSubmitSlot = false,
  submitTooltip = "Sign In",
  submitLoading = false,
  inlineRules = null,
}) {
  const [visible, setVisible] = useState(false);

  const iconColor = "rgba(190,214,211,0.70)";

  const fieldClassName = [
    "mg-password-field",
    noBottomMargin ? "no-bottom-margin" : "",
    (showSubmit || reserveSubmitSlot) ? "has-submit" : "",
    inlineRules ? "has-inline-rules" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={fieldClassName}>
      <Lock
        size={18}
        style={{
          position: "absolute",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          color: "rgba(190,214,211,0.58)",
          zIndex: 10,
          pointerEvents: "none",
        }}
      />

      <input
        id={id}
        type={visible ? "text" : "password"}
        disabled={disabled}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        required
      />

      {inlineRules && (
        <div className="mg-password-inline-rules" aria-live="polite">
          <span className="mg-password-inline-rules-list">
            {inlineRules.map((rule) => (
              <span
                key={rule.label}
                title={rule.title}
                aria-label={rule.title}
                className={[
                  "mg-password-inline-rule",
                  rule.met ? "met" : "",
                  rule.label === "*" ? "special" : "",
                ].filter(Boolean).join(" ")}
              >
                {rule.label}
              </span>
            ))}
          </span>
        </div>
      )}

      <div
        className={["mg-password-actions", showForgot ? "has-forgot" : "", (showSubmit || reserveSubmitSlot) ? "has-submit" : ""].filter(Boolean).join(" ")}
        aria-hidden={false}
      >
        {(showSubmit || reserveSubmitSlot) && (
          <>
            {showSubmit ? (
              <button
                type="submit"
                className="mg-password-icon-btn mg-submit-icon-btn"
                disabled={disabled}
                aria-label={submitTooltip}
                data-tooltip={submitTooltip}
              >
                {submitLoading ? (
                  <LoaderCircle className="mg-submit-spinner" size={17} color={iconColor} strokeWidth={2.45} />
                ) : (
                  <ArrowRight size={17} color={iconColor} strokeWidth={2.65} />
                )}
              </button>
            ) : (
              <span className="mg-password-icon-btn mg-submit-icon-btn mg-submit-icon-placeholder" aria-hidden="true" />
            )}
            <span
              className="mg-password-action-divider"
              aria-hidden="true"
              style={{ opacity: showSubmit ? 1 : 0 }}
            />
          </>
        )}

        <button
          type="button"
          className="mg-password-icon-btn"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? "Hide password" : "Show password"}
          data-tooltip={visible ? "Hide Password" : "Show Password"}
        >
          {visible ? <EyeOff size={18} color={iconColor} /> : <Eye size={18} color={iconColor} />}
        </button>

        {showForgot && (
          <>
            <span className="mg-password-action-divider" aria-hidden="true" />
            <button
              type="button"
              className="mg-password-icon-btn mg-forgot-icon-btn"
              onClick={onForgot}
              disabled={disabled}
              aria-label="Forgot Password"
              data-tooltip="Forgot Password"
            >
              ?
            </button>
          </>
        )}
      </div>
    </div>
  );
}


export default function Login() {
  const { refreshUser, currentUser, loadingUser, userError } = useAuth();
  const location = useLocation();
  const [step, setStep] = useState("login");
  const [isWarping, setIsWarping] = useState(false);
  const [mode, setMode] = useState("login"); // login | signup
  const [modeStep, setModeStep] = useState("form"); // "form" | "forgot" | "confirm" | "mfaChoice" | "totp" | "adminTotp"
  const destinationRef = useRef(null);
  const finishWarp = useCallback(() => setIsWarping(false), []);

  // shared
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // login
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [, setLoginPasswordActive] = useState(false);

  // signup
  const [password2, setPassword2] = useState("");
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");

  // email verification
  const [verifyCode, setVerifyCode] = useState("");

  // forgot password flow
  const [resetCode, setResetCode] = useState("");
  const [newPass, setNewPass] = useState("");

  // TOTP setup
  const [authUser, setAuthUser] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  // MFA login
  const [pendingMfaUser, setPendingMfaUser] = useState(null);
  const [mfaCode, setMfaCode] = useState("");


  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (currentUser && params.get("menu") === "1"
      && modeStep === "form" && !pendingMfaUser && localStorage.getItem("admin_mfa_started") !== "true") setStep("menu");
    // Invite-only signup: /login?invite=1 shows the hidden signup form.
    if (params.get("invite") === "1" && mode === "login" && modeStep === "form") setMode("signup");
  }, [currentUser, location.search, modeStep, pendingMfaUser, mode]);
  useEffect(() => {
    // Remember me: persist last email + restore pending verify email
    const saved = localStorage.getItem("mg_last_email");
    if (saved) setEmail(saved);
    const pending = localStorage.getItem("mg_pending_email");
    if (pending) {
      setEmail(pending);
      setMode("signup");
    }
  }, []);
  useEffect(() => {
    if (remember && email) localStorage.setItem("mg_last_email", email);
  }, [remember, email]);


  const norm = (v) => (v || "").trim().toLowerCase();

  function resetNotice(e) {
    if (e) e.preventDefault();
    setErr("");
    setOk("");
  }


  function startWarpToMenu() {
    setErr("");
    setOk("");
    setLoginPasswordActive(false);
    setIsWarping(document.visibilityState === "visible" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    setStep("menu");
  }

  /** Handlers */
  async function handleLogin(e) {
    resetNotice(e);

    if (!norm(email)) {
      setErr("Please enter your email address.");
      return;
    }

    if (!password.trim()) {
      setErr("Please enter your password.");
      return;
    }

    setLoading(true);
    try {
      localStorage.removeItem("admin_mfa_started");
      const result = await signIn(norm(email), password);
      localStorage.setItem("mg_session_hint", "1");
      localStorage.removeItem("mg_pending_email");
      if (result.challengeName === "SOFTWARE_TOKEN_MFA") {
        setOk("Enter 6-digit code from your authenticator app.");
        setPendingMfaUser(result.user);
        setModeStep("totp");
        localStorage.setItem("pref_mfa", true);
      } else {
        if (remember) localStorage.setItem("mg_session_hint", "1");
        const token = result.user.signInUserSession.idToken.jwtToken;
        const isAdmin = await checkIsAdmin(token);
        if (isAdmin) {
          localStorage.setItem("admin_mfa_started", true);
          setAuthUser(result.user);
          const secret = await setupTOTP(result.user);
          setTotpSecret(secret);
          setMode("login");
          setModeStep("adminTotp");
          setOk(
            "Please secure your admin account by setting up Multi-Factor Authentication before continuing."
          );
        } else {
          await refreshUser(true);
          localStorage.setItem("pref_mfa", false);
          startWarpToMenu();
        }
      }
    } catch (e) {
      localStorage.removeItem("mg_session_hint");
      localStorage.setItem("mg_pending_email", norm(email));
      if (e.code === "UserNotConfirmedException") {
        setMode("login");
        setModeStep("confirm");
        handleResend();
      } else {
        const message = mapCognitoError(e, "login");
        if (
          message === "Incorrect username or password." ||
          e.code === "NotAuthorizedException" ||
          e.code === "UserNotFoundException" ||
          e.name === "UserNotFoundException"
        ) {
          setErr("Incorrect username or password. Try again or create an account below.");
        } else {
          setErr(message);
        }
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSignup(e) {
    resetNotice(e);
    localStorage.removeItem("admin_mfa_started");

    if (!fullName.trim()) return setErr("Please enter your full name.");
    if (!company.trim()) return setErr("Please enter your company.");
    if (!norm(email)) return setErr("Please enter your email address.");
    if (!password.trim()) return setErr("Please enter a password.");
    if (!password2.trim()) return setErr("Please confirm your password.");
    if (password !== password2) return setErr("Passwords do not match.");
    setLoading(true);
    try {
      await signUp({ email: norm(email), password, name: fullName, company });
      setOk("We emailed you a verification code. Enter it below to confirm.");
      setModeStep("confirm");
    } catch (e) {
      setErr(mapCognitoError(e, "signup"));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm(e) {
    resetNotice(e);

    if (!verifyCode.trim()) {
      setErr("Please enter the verification code.");
      return;
    }

    setLoading(true);
    try {
      await confirmSignUp(norm(email), verifyCode.trim());
      if (mode === "signup") {
        setModeStep("mfaChoice");
      } else {
        handleLogin(e);
      }
    } catch (e) {
      setErr(mapCognitoError(e, "confirm"));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend(e) {
    resetNotice(e);
    setLoading(true);
    try {
      await resendConfirmationCode(norm(email));
      setOk("Verification code re-sent to your email.");
    } catch (e) {
      setErr(mapCognitoError(e, "confirm"));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotStart() {
    resetNotice();
    setLoading(true);
    try {
      await forgotPassword(norm(email));
      setOk("Password reset code sent. Enter it below with your new password.");
      setModeStep("forgot");
    } catch (e) {
      setErr(mapCognitoError(e, "forgot"));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotSubmit(e) {
    resetNotice(e);

    if (!resetCode.trim()) {
      setErr("Please enter the reset code.");
      return;
    }

    if (!newPass.trim()) {
      setErr("Please enter your new password.");
      return;
    }

    setLoading(true);
    try {
      await forgotPasswordSubmit(norm(email), resetCode.trim(), newPass);
      setOk("Password changed. Please sign in.");
      setMode("login");
      setModeStep("form");
    } catch (e) {
      setErr(mapCognitoError(e, "forgot"));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyTOTP(e) {
    resetNotice(e);
    setLoading(true);
    try {
      await confirmSignInWithTOTP(pendingMfaUser, mfaCode);
      localStorage.removeItem("mg_pending_email");
      await refreshUser(true);
      startWarpToMenu();
    } catch {
      setErr("Invalid MFA code, please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyAndEnableTOTP(e) {
    resetNotice(e);
    setLoading(true);
    try {
      await verifyTOTP(authUser, totpCode);
      await enableTOTP(authUser);
      localStorage.setItem("mg_session_hint", "1");
      localStorage.setItem("pref_mfa", true);
      localStorage.removeItem("mg_pending_email");
      await refreshUser(true);
      localStorage.removeItem("admin_mfa_started");
      startWarpToMenu();
    } catch {
      setErr("Invalid code, please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleEnableMfaChoice(enable) {
    setLoading(true);
    const { user } = await signIn(norm(email), password);
    setAuthUser(user);
    if (enable) {
      const secret = await setupTOTP(user);
      setTotpSecret(secret);
      setModeStep("totp");
    } else {
      localStorage.setItem("mg_session_hint", "1");
      localStorage.setItem("pref_mfa", false);
      localStorage.removeItem("mg_pending_email");
      await refreshUser(true);
      startWarpToMenu();
    }
    setLoading(false);
  }

  function handleBack(step) {
    setModeStep(step);
    setLoginPasswordActive(false);
    setErr("");
    setOk("");
  }

  const signupReady = !!(norm(email) && password.trim() && password2.trim());
  const signupButtonVisible = !!(norm(email) && password.trim() && password2.length > 0);
  const verifyReady = verifyCode.trim().length === 6;
  const mfaReady = mfaCode.length === 6;
  const totpSetupReady = totpCode.trim().length === 6;

  const passwordRules = [
    { label: "8+", title: "8+ characters", met: password.length >= 8 },
    { label: "Aa", title: "uppercase letter", met: /[A-Z]/.test(password) },
    { label: "aa", title: "lowercase letter", met: /[a-z]/.test(password) },
    { label: "123", title: "number", met: /\d/.test(password) },
    { label: "*", title: "special character", met: /[^A-Za-z0-9]/.test(password) },
  ];


  const selectorVisible = step === "menu" && !!currentUser && !loadingUser;
  const primary = mode === "login" && modeStep === "form";
  useEffect(() => {
    if (selectorVisible && !isWarping) destinationRef.current?.querySelector(".lt-screen")?.focus({ preventScroll: true });
  }, [selectorVisible, isWarping]);
  return <>
    <div ref={destinationRef} className="v6-auth-destination"
      aria-hidden={!selectorVisible || isWarping} inert={!selectorVisible || isWarping ? "" : undefined}
      style={{ visibility: selectorVisible && !isWarping ? "visible" : "hidden", opacity: selectorVisible && !isWarping ? 1 : 0 }}>
      {selectorVisible && <LoginTest enterAnimation fromLogin />}
    </div>
    {(!selectorVisible || isWarping) && <VaultLoginShell exiting={selectorVisible && isWarping}
      onExited={finishWarp} destinationRef={destinationRef} primary={primary}>
      {step === "menu" && !selectorVisible && <p role={userError ? "alert" : "status"}>
        {loadingUser ? "Restoring your session..." : userError || "Your session could not be restored. Please sign in again."}
      </p>}
      <fieldset className="mgr-auth-fields" disabled={loading || selectorVisible}
        aria-busy={loading}>
        <div className="mg-auth-minimal-panel">
              {!primary && <p className="tv2-kicker">01 / ACCOUNT ACCESS</p>}
              {!primary && (err || ok) && !(mode === "login" && modeStep === "totp") && (
                <div className="mgr-auth-notice" role={err ? "alert" : "status"}>{err || ok}</div>
              )}

              {/* LOGIN */}
              {mode === "login" && modeStep === "form" && (
                <>
                  <form className="tv2-form" onSubmit={handleLogin} noValidate>
                    <p className="tv2-kicker v6-resolve" style={{ "--resolve-delay": "1160ms" }}>01 / ACCOUNT ACCESS</p>
                    <label className="tv2-field v6-resolve" style={{ "--resolve-delay": "1250ms" }}>
                      <span>EMAIL</span>
                      <div>
                        <Mail size={15} aria-hidden="true" />
                        <input id="login-email" value={email} onChange={e => setEmail(e.target.value)}
                          placeholder="you@company.com" type="email" autoComplete="email" required />
                        <b aria-hidden="true">+</b>
                      </div>
                    </label>
                    <div className="tv2-field v6-resolve" style={{ "--resolve-delay": "1380ms" }}>
                      <label htmlFor="login-password" className="v6-password-label">PASSWORD</label>
                      <MgPasswordField
                        id="login-password"
                        value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="Your password" autoComplete="current-password" ariaLabel="Password"
                        disabled={loading} onFocus={() => setLoginPasswordActive(true)} noBottomMargin
                      />
                    </div>
                    <div className="tv2-options v6-resolve" style={{ "--resolve-delay": "1500ms" }}>
                      <label><input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />Remember me</label>
                      <button type="button" onClick={handleForgotStart} disabled={loading}>Forgot password?</button>
                    </div>
                    <button type="submit" className="tv2-signin v6-resolve" style={{ "--resolve-delay": "1580ms" }} disabled={loading}>
                      <span>{loading ? "SIGNING IN..." : "Sign in"}</span><span aria-hidden="true">→</span>
                    </button>
                    <p className="tv2-notice v6-resolve" style={{ "--resolve-delay": "1700ms" }}
                      role={err ? "alert" : "status"}>{err || ok}</p>
                  </form>
                  <div className="tv2-under v6-resolve" style={{ "--resolve-delay": "1780ms" }}>
                    <span>New to mindGAME?</span>
                    <button
                      type="button"
                      className="v6-create-account"
                      onClick={() => {
                        setMode("signup");
                        handleBack("form");
                      }}
                      disabled={loading}
                    >
                      Create an account ↗
                    </button>
                    <a
                      className="v6-create-account v6-tableau-access"
                      href="/tableau"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Access mindGAME Tableau ↗
                    </a>
                  </div>
                </>
              )}

              {mode === "login" && modeStep === "forgot" && (
                <form onSubmit={handleForgotSubmit} noValidate>
                  <div className="mg-auth-minimal-title">Reset Password</div>

                  <div className="mg-input-wrap" >
                    <Hash size={18}  />
                    <input
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      placeholder="Enter reset code from email"

                      required
                    />
                  </div>

                  <MgPasswordField
                    value={newPass}
                    onChange={(e) => setNewPass(e.target.value)}
                    placeholder="New Password"
                    autoComplete="new-password"
                    ariaLabel="New password"
                    disabled={loading}
                  />

                  <button type="submit" className="mg-form-button"  disabled={loading}>
                    Set Password
                  </button>

                  <button
                    type="button"
                    onClick={() => handleBack("form")}
                    className="mg-auth-mode-link"
                    disabled={loading}
                    style={{ marginTop: 14 }}
                  >
                    ← Back to sign in
                  </button>
                </form>
              )}

              {mode === "login" && modeStep === "totp" && (
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!loading) handleVerifyTOTP(e);
                  }}
                >
                  <div className="mg-mfa-instruction-row" role={err ? "alert" : "status"} style={{ height: 47, marginBottom: 18, display: "flex", alignItems: "center", gap: 9, padding: "0 14px", boxSizing: "border-box", color: err ? "#ffb2aa" : "rgba(190,214,211,0.78)", fontSize: 13, lineHeight: 1.35 }}>
                    <Hash size={15} style={{ flexShrink: 0, color: "rgba(190,214,211,0.56)" }} />
                    <span>{err || "Enter 6-digit code from your authenticator app."}</span>
                  </div>
                  <div className="mg-input-wrap" >
                    <Hash size={18}  />
                    <input
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="123456"

                      inputMode="numeric"
                      autoFocus
                      autoComplete="one-time-code"
                      aria-label="Authenticator Code"
                      required
                    />
                    {mfaReady && (
                      <button
                        type="submit"
                        className="mg-password-icon-btn mg-submit-icon-btn mg-code-submit-btn"
                        disabled={loading}
                        aria-label={loading ? "Verifying" : "Sign In"}
                        data-tooltip={loading ? "Verifying" : "Sign In"}
                      >
                        {loading ? (
                          <LoaderCircle className="mg-submit-spinner" size={17} color="rgba(190,214,211,0.70)" strokeWidth={2.45} />
                        ) : (
                          <ArrowRight size={17} color="rgba(190,214,211,0.70)" strokeWidth={2.65} />
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleBack("form")}
                    className="mg-auth-mode-link"
                    disabled={loading}
                    style={{ marginTop: 14 }}
                  >
                    ← Back to sign in
                  </button>
                </form>
              )}

              {/* SIGNUP */}
              {mode === "signup" && modeStep === "form" && (
                <>
                  <form onSubmit={handleSignup} noValidate>
                    <label className="mgr-field-label" htmlFor="signup-name">FULL NAME</label>
                    <div className="mg-input-wrap" >
                      <User size={18}  />
                      <input
                        id="signup-name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your Name"

                        required
                      />
                    </div>

                    <label className="mgr-field-label" htmlFor="signup-company">COMPANY</label>
                    <div className="mg-input-wrap" >
                      <Building2 size={18}  />
                      <input
                        id="signup-company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Company Name"

                        required
                      />
                    </div>

                    <label className="mgr-field-label" htmlFor="signup-email">EMAIL</label>
                    <div className="mg-input-wrap" >
                      <Mail size={18}  />
                      <input
                        id="signup-email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"

                        type="email"
                        autoComplete="email"
                        required
                      />
                    </div>

                    <label className="mgr-field-label" htmlFor="signup-password">PASSWORD</label>
                    <MgPasswordField
                      id="signup-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create Password"
                      autoComplete="new-password"
                      ariaLabel="Create password"
                      disabled={loading}
                      inlineRules={passwordRules}
                    />

                    <label className="mgr-field-label" htmlFor="signup-password-confirm">CONFIRM PASSWORD</label>
                    <MgPasswordField
                      id="signup-password-confirm"
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      placeholder="Re-enter Password"
                      autoComplete="new-password"
                      ariaLabel="Confirm password"
                      disabled={loading}
                    />

                    {signupButtonVisible && (
                      <button
                        type="submit"
                        className={signupReady ? "mg-form-button mg-form-button-active" : "mg-form-button"}

                        disabled={loading}
                      >
                        {loading ? "Creating…" : "Create Account"}
                      </button>
                    )}
                  </form>

                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <button
                      type="button"
                      className="mg-auth-mode-link"
                      onClick={() => {
                        setMode("login");
                        handleBack("form");
                      }}
                      disabled={loading}
                    >
                      Already have access? Sign in<span className="mg-mode-chevrons" aria-hidden="true">››</span>
                    </button>
                  </div>
                </>
              )}

              {modeStep === "confirm" && (
                <form
                  className="mg-auth-sync-fade"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!loading && verifyReady) handleConfirm(e);
                  }}
                  noValidate
                >
                  <div className="mg-input-wrap" >
                    <Hash size={18}  />
                    <input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="Enter verification code from email"

                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                    {verifyReady && (
                      <button
                        type="submit"
                        className="mg-password-icon-btn mg-submit-icon-btn mg-code-submit-btn"
                        disabled={loading}
                        aria-label={loading ? "Confirming" : "Confirm Email"}
                        data-tooltip={loading ? "Confirming" : "Confirm"}
                      >
                        {loading ? (
                          <LoaderCircle className="mg-submit-spinner" size={17} color="rgba(190,214,211,0.70)" strokeWidth={2.45} />
                        ) : (
                          <ArrowRight size={17} color="rgba(190,214,211,0.70)" strokeWidth={2.65} />
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleResend}
                    className="mg-auth-mode-link"
                    style={{ marginTop: 14 }}
                    disabled={loading}
                  >
                    ↻ Resend verification code
                  </button>
                </form>
              )}

              {modeStep === "mfaChoice" && mode === "signup" && (
                <div style={{ textAlign: "center", padding: "4px 0 2px" }}>
                  <div className="mg-auth-minimal-title">Multi-Factor Authentication (MFA)</div>

                  <img
                    src="/MFA Icon.png"
                    alt="Multi-Factor Authentication"
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: "contain",
                      display: "block",
                      margin: "-4px auto 14px",
                      filter: "drop-shadow(0 14px 30px rgba(0,0,0,0.38)) drop-shadow(0 0 18px rgba(190,214,211,0.10))",
                      opacity: 0.96,
                    }}
                  />

                  <p
                    style={{
                      maxWidth: 380,
                      margin: "0 auto 18px",
                      color: "rgba(190,214,211,0.76)",
                      fontSize: 13,
                      lineHeight: 1.45,
                    }}
                  >
Protect your account with a one-time code from any authenticator app, such as Google Authenticator, Microsoft Authenticator, or 1Password. Enable MFA now or skip to continue.
                  </p>

                  <div className="mg-mfa-choice-row" style={{ display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="mg-form-button mg-mfa-primary-choice"

                      disabled={loading}
                      onClick={() => handleEnableMfaChoice(true)}
                    >
                      Enable MFA
                    </button>
                    <button
                      type="button"
                      className="mg-form-button"

                      disabled={loading}
                      onClick={() => handleEnableMfaChoice(false)}
                    >
                      Skip
                    </button>
                  </div>
                </div>
              )}

              {((mode === "signup" && modeStep === "totp") ||
                (mode === "login" && modeStep === "adminTotp")) && (
                <form noValidate onSubmit={handleVerifyAndEnableTOTP} style={{ textAlign: "center" }}>
                  <div className="mg-auth-minimal-title">Authenticator</div>

                  <p
                    style={{
                      maxWidth: 380,
                      margin: "0 auto 14px",
                      color: "rgba(190,214,211,0.76)",
                      fontSize: 12.5,
                      lineHeight: 1.42,
                    }}
                  >
                    <>
                      Scan the QR code using your authenticator app,
                      <br />
                      then enter the 6-digit code.
                    </>
                  </p>

                  <div
                    style={{
                      display: "inline-block",
                      padding: 10,
                      background: "#fff",
                      borderRadius: 12,
                      marginBottom: 16,
                      boxShadow: "0 18px 46px rgba(0,0,0,0.34)",
                    }}
                  >
                    <QRCodeSVG
                      value={`otpauth://totp/MindGame:${encodeURIComponent(
                        email
                      )}?secret=${totpSecret}&issuer=MindGame`}
                      size={190}
                    />
                  </div>

                  <div className="mg-input-wrap" >
                    <Hash size={18}  />
                    <input
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="Enter 6-digit authenticator code"

                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className={totpSetupReady ? "mg-form-button mg-form-button-active" : "mg-form-button"}

                    disabled={loading || totpCode.trim().length < 6}
                  >
                    {loading ? "Verifying…" : "Verify & Enable"}
                  </button>
                </form>
              )}
            </div>
      </fieldset>
    </VaultLoginShell>}
  </>;
}
